import React, { useState, useEffect } from 'react'
import { Formik, Field, Form, ErrorMessage } from 'formik';
import { Eye, EyeOff } from 'lucide-react';
import Logo from './../images/Logo.webp';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useNavigate } from "react-router-dom";
import AuthIllustration from '../components/AuthIllustration';

function PasswordReset() {
    const navigate = useNavigate();
    const [key, setKey] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    
    const initialValues = {       
        password: '',
        confirmPassword:''
    };
    
    const validationSchema = Yup.object().shape({    
        password: Yup.string()
            .min(8, 'Password must be 8 characters long')
            .required('*Password is required'),
        confirmPassword: Yup.string()
            .required('*Password is required')
            .oneOf([Yup.ref('password'), null], 'Passwords do not match')
    });

    const resetPassword = async (values, { setSubmitting }) => { 
        setSubmitting(true);
        try{
            await axios.post(`${process.env.REACT_APP_BASE_URL}/reset`, {                    
                password: values.confirmPassword,
                key: key
            })
            setSubmitting(false);    
            toast.success("Password updated successfully")
            setTimeout(()=>{
                navigate("/login")
            },2000)                
        }catch(error){
            setSubmitting(false);
            console.log(error)
            let errmsg = (error?.response) ? (error?.response?.data?.message ? error?.response?.data?.message : `${error?.response?.status} - ${error?.response?.statusText}`) : "Something went wrong. Please try again later"
            toast.error(errmsg)
        }          
    }
    
    useEffect(()=>{
        let requestKey = new URLSearchParams(document.location.search).get("key");
        setKey(requestKey)
    },[])

    return (
        <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden px-4 py-6 sm:px-6 lg:px-8">
            <div className="relative z-10 mx-auto grid w-full max-w-6xl gap-8 xl:gap-16 lg:grid-cols-2 lg:items-center">
                {/* Left side text */}
                <div className="hidden lg:flex flex-col lg:pl-10 xl:pl-16 w-fit">
                    <div className="flex items-center justify-start w-full mb-8">
                        <img src={Logo} alt="Data Aces Logo" className="h-10" />
                    </div>
                    
                    <h1 className="text-4xl font-bold text-slate-800 mb-2">Create new password</h1>
                    <h2 className="text-3xl font-bold text-orange-500 mb-4 flex items-center gap-2">Secure your account 🛡️</h2>
                    <div className="w-12 h-1 bg-orange-500 mb-6 rounded-full"></div>
                    
                    <p className="text-slate-500 mb-8 text-base">
                        Your new password must be at least 8 characters long.<br/>
                        Make sure it is unique and difficult to guess.
                    </p>
                </div>

                {/* Right side form */}
                <div className="self-center bg-white rounded-2xl border border-slate-200 shadow-sm p-8 md:p-10 w-full max-w-md mx-auto relative">
                    <div className="flex justify-center mb-6 lg:hidden">
                        <img src={Logo} alt="Data Aces Logo" className="h-10" />
                    </div>
                    
                    <h2 className="text-2xl font-bold text-slate-800 mb-2">Reset Password</h2>
                    <p className="text-slate-500 mb-6 text-sm">Please enter your new password below</p>

                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={resetPassword}
                    >
                        {({ isSubmitting }) => (
                            <Form className="space-y-4">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-800 mb-2">New Password</label>
                                    <div className="relative">
                                        <div className="absolute top-1/2 -translate-y-1/2 left-0 w-11 flex items-center justify-center pointer-events-none">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                                        </div>
                                        <Field type={showPassword ? "text" : "password"} name="password" placeholder="Enter new password" className="w-full pl-11 pr-11 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-blue-500 transition-colors" />
                                        <div className="absolute top-1/2 -translate-y-1/2 right-0 w-11 flex items-center justify-center cursor-pointer text-slate-400 hover:text-slate-600 transition-colors" onClick={() => setShowPassword(!showPassword)}>
                                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </div>
                                    </div>
                                    <ErrorMessage name="password" component="div" className="text-red-500 text-xs mt-1"/>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-slate-800 mb-2">Confirm Password</label>
                                    <div className="relative">
                                        <div className="absolute top-1/2 -translate-y-1/2 left-0 w-11 flex items-center justify-center pointer-events-none">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                                        </div>
                                        <Field type={showConfirmPassword ? "text" : "password"} name="confirmPassword" placeholder="Confirm new password" className="w-full pl-11 pr-11 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-blue-500 transition-colors" />
                                        <div className="absolute top-1/2 -translate-y-1/2 right-0 w-11 flex items-center justify-center cursor-pointer text-slate-400 hover:text-slate-600 transition-colors" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                            {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </div>
                                    </div>
                                    <ErrorMessage name="confirmPassword" component="div" className="text-red-500 text-xs mt-1"/>
                                </div>

                                <div className="pt-2">
                                    <button type="submit" disabled={isSubmitting} className="w-full flex items-center justify-center gap-2 py-3 bg-[#2563eb] text-white rounded-xl hover:bg-blue-700 transition-colors font-semibold text-sm shadow-[0_4px_12px_rgba(37,99,235,0.2)]" style={{opacity: isSubmitting ? '0.7' : '1'}}>
                                        {isSubmitting ? (
                                            <svg className="animate-spin mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2v6h-6"/><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M3 22v-6h6"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/></svg>
                                        )}
                                        {isSubmitting ? 'Resetting...' : 'Reset Password'}
                                    </button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
            
            <div className="fixed bottom-6 right-8 text-xs text-slate-400 font-medium flex items-center gap-1.5 z-20">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                © {new Date().getFullYear()} Data Aces. All rights reserved.
            </div>

            <AuthIllustration />
        </div>
    )   
}

export default PasswordReset
